import PropTypes from 'prop-types';
import { use, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { listContentTypes, postWebhooks } from '../../lib/flotiq-client';
import {
  ResponseError,
  checkResponseStatus,
} from '../../lib/flotiq-client/response-errors';
import { FormAddSidebarPanelEvent } from '../../lib/flotiq-plugins/plugin-events/FormAddSidebarPanelEvent';
import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import ContentObjectInformations from '../../components/ContentObjectInformations/ContentObjectInformations';
import ContentObjectLogs from '../../components/ContentObjectLogs/ContentObjectLogs';
import ContentObjectVersions from '../../components/ContentObjectVersions/ContentObjectVersions';
import { useGridNavigate } from '../../components/DataGrid/useGridFilters';
import ElementFromPlugin from '../../components/ElementFromPlugin/ElementFromPlugin';
import Loader from '../../components/Loader/Loader';
import TopbarBreadcrumbs from '../../components/Topbar/breadcrumbs/TopbarBreadcrumbs';
import TopbarCancelButton from '../../components/Topbar/buttons/TopbarCancelButton';
import TopbarDeleteButton from '../../components/Topbar/buttons/TopbarDeleteButton';
import TopbarSaveButton from '../../components/Topbar/buttons/TopbarSaveButton';
import TopbarActionMenu from '../../components/Topbar/buttons/base/TopbarActionMenu';

import {
  useContentObject,
  useContentObjectVersions,
  useContentType,
  useContentTypes,
  usePluginsSettings,
  useWebhookLogs,
} from '../../hooks/api';
import { useAllSpaceConstraints } from '../../hooks/api/useAllSpaceConstraints';
import useApiErrorsToast from '../../hooks/api/useApiErrorsToast';
import usePluginResults from '../../hooks/usePluginResults';
import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';
import UserContext from '../../contexts/UserContext';

import WebhookForm from '../../form/WebhookForm/WebhookForm';
import PageLayout, {
  predefinedLayoutClasses,
} from '../../layout/PageLayout/PageLayout';

const DEFAULT_CODE_EDITOR =
  'function _webhook(ctx) {\n' +
  '        const { payload } = ctx;\n' +
  '        const newPayload = { ...payload };\n\n' +
  '        // Example: Payload validation\n' +
  "        // if ( payload['category'] === 'book' && !payload['isbn'])\n" +
  "        // return { errors: {'isbn': ['Error: ISBN is required for books.'] } };\n\n" +
  '        // Example: Payload modifications\n' +
  "        // newPayload['slug'] = payload['title']\n" +
  '        //     .toLowerCase()\n' +
  "        //     .replace(/[^a-z0-9]+/g, '-')\n" +
  "        //     .replace(/(^-|-$)/g, '');\n\n" +
  '        return { payload: newPayload };\n' +
  '}';

const USER_PLUGINS_PARAMS = {
  limit: 1000,
  page: 1,
};

const VERSION_HOOK_PARAMS = {
  limit: 1000,
  page: 1,
};

const AddWebhooks = ({ mode = 'edit', duplicate = false, testId = '' }) => {
  const { t } = useTranslation();
  const { space, buildUrlWithSpace, spaceLimits } = useSelectedSpace();
  const { permissions } = use(UserContext);
  const jwt = useToken();
  let { id } = useParams();
  const modal = useModals();

  const [formState, setFormState] = useState();
  const [isSaving, setIsSaving] = useState(false);

  const { navigateGrid, gridLink } = useGridNavigate(
    `objects-_webhooks`,
    buildUrlWithSpace(`webhooks`),
  );

  const {
    entity: webhook,
    isLoading: webhookLoading,
    errors,
    deleteEntity: deleteWebhook,
    updateEntity: updateWebhook,
    partialUpdateEntity: partialUpdateWebhook,
  } = useContentObject('_webhooks', id);

  useApiErrorsToast(errors);

  useEffect(() => {
    if (id && !webhook && !webhookLoading) {
      navigateGrid();
    }
  }, [webhookLoading, navigateGrid, webhook, id]);

  const versionsHookOptions = useMemo(
    () => ({ pause: !id || duplicate }),
    [duplicate, id],
  );

  const webhookLogsParams = useMemo(
    () => ({
      id,
    }),
    [id],
  );

  const {
    data: webhookLogs,
    isLoading: webhookLogsLoading,
    reload: webhookLogsReload,
    errors: webhookLogsErrors,
  } = useWebhookLogs(webhookLogsParams, versionsHookOptions);

  useApiErrorsToast(webhookLogsErrors);

  const { data: userPlugins } = usePluginsSettings(USER_PLUGINS_PARAMS);

  const { entity: webhookContentType, isLoading: webhookContentTypeLoading } =
    useContentType('_webhooks');

  const { canCreate, canDelete, canUpdate } = useMemo(
    () => permissions.getCoPermissions('_webhooks') || {},
    [permissions],
  );

  const codeWebhookLimit = spaceLimits?.hosted_webhooks_limit;

  const { allConstraints, reload: relaodAllConstraints } =
    useAllSpaceConstraints();

  const hostedWebhooksCount = allConstraints?.['hosted-webhooks-count'];

  const reloadWebhookConstraints = useCallback(() => {
    relaodAllConstraints([
      'hosted-webhooks-count',
      'content-objects-count',
      'webhooks-count',
    ]);
  }, [relaodAllConstraints]);

  const codeWebhookAvailable = useMemo(
    () =>
      codeWebhookLimit !== -1 ? hostedWebhooksCount < codeWebhookLimit : true,
    [codeWebhookLimit, hostedWebhooksCount],
  );

  const {
    data: webhookVersions,
    errors: webhookVersionsErrors,
    reload: reloadWebhookVersions,
  } = useContentObjectVersions(
    '_webhooks',
    id,
    VERSION_HOOK_PARAMS,
    versionsHookOptions,
  );

  useApiErrorsToast(webhookVersionsErrors);

  const disableSave =
    !codeWebhookAvailable && formState?.values?.executionMode === 'code';

  const handleDeleteWebhook = useCallback(async () => {
    const result = await modal.delete(t('Webhooks.DeleteModalText'));

    if (result) {
      try {
        const { body, status } = await deleteWebhook({
          contentTypeName: '_webhooks',
        });
        checkResponseStatus(body, status);

        navigateGrid();
        reloadWebhookConstraints();
      } catch (error) {
        if (!(error instanceof ResponseError)) {
          toast.error(t('Webhooks.Error'));
        } else {
          toast.error(error.message);
        }
      }
    }
  }, [modal, t, deleteWebhook, navigateGrid, reloadWebhookConstraints]);

  const validateCodeEditorValue = useCallback((code, type, mode) => {
    // Case: code value should be empty if
    // - default editor code was not changed by the user and
    //   execution mode is switch to "url"
    // - when selected type is not 'sync'
    if (type !== 'sync' || (mode === 'url' && code === DEFAULT_CODE_EDITOR)) {
      return '';
    }
    return code;
  }, []);

  const handleSave = useCallback(
    async (values) => {
      setIsSaving(true);

      let updateShape = {
        name: values?.name,
        url: values?.url,
        enabled: values?.enabled,
        type: values?.type,
        executionMode: values?.executionMode || 'url',
        code: validateCodeEditorValue(
          values?.code,
          values?.type,
          values?.executionMode,
        ),
        headers: values?.headers,
        ...(values?.type !== 'ctd'
          ? {
              actions: values?.actions,
              content_type_definitions: values?.contentTypeDefinitions?.map(
                (content_type_definition_name) => {
                  return { content_type_definition_name };
                },
              ),
            }
          : { actions: [], content_type_definitions: [] }),
      };

      for (const property in updateShape) {
        if (
          !permissions.canCo(
            '_webhooks',
            mode === 'edit'
              ? RolePermissions.PERMISSIONS_TYPES.UPDATE
              : RolePermissions.PERMISSIONS_TYPES.CREATE,
            property,
          )
        ) {
          delete updateShape[property];
        }
      }

      if (mode === 'edit' && !duplicate) {
        const updateFunc = permissions.hasCoLimitedFields('_webhooks')
          ? partialUpdateWebhook
          : updateWebhook;

        try {
          const { body, status } = await updateFunc({
            ...updateShape,
            id,
            contentTypeName: '_webhooks',
          });
          setIsSaving(false);

          checkResponseStatus(body, status);

          await reloadWebhookVersions();
          toast.success(`${t('Webhooks.SavedOnSucces')}`);
          navigateGrid();
          return [body, null];
        } catch (error) {
          if (!(error instanceof ResponseError)) {
            toast.error(t('Webhooks.SavedOnError'));
            return [null, {}];
          } else {
            toast.error(error.message);
          }
          return [null, error];
        }
      } else {
        try {
          const { body, status } = await postWebhooks(jwt, space, updateShape);
          setIsSaving(false);
          checkResponseStatus(body, status);

          toast.success(`${t('Webhooks.AddedOnSucces')}`);
          navigateGrid();
          reloadWebhookConstraints();
          return [body, null];
        } catch (error) {
          if (!(error instanceof ResponseError)) {
            toast.error(t('Webhooks.SavedOnError'));
            return [null, {}];
          } else {
            toast.error(error.message);
            return [null, error];
          }
        }
      }
    },
    [
      validateCodeEditorValue,
      mode,
      duplicate,
      permissions,
      partialUpdateWebhook,
      updateWebhook,
      id,
      reloadWebhookVersions,
      t,
      navigateGrid,
      jwt,
      space,
      reloadWebhookConstraints,
    ],
  );

  const ctdParams = useMemo(
    () => ({
      limit: 1000,
      order_by: 'label',
      order_direction: 'asc',
      page: 1,
    }),
    [],
  );

  const {
    data: contentTypes,
    isLoading: isLoadingCT,
    errors: ctdErrors,
  } = useContentTypes(ctdParams);

  useApiErrorsToast(ctdErrors);

  const contentTypesOptions = useMemo(() => {
    if (!contentTypes) return;

    return contentTypes
      .filter(
        (el) => !(el.internal && el.name !== '_tag' && el.name !== '_media'),
      )
      .map((el) => {
        return {
          value: el.name,
          label: el.label,
        };
      });
  }, [contentTypes]);

  const filterCtdName = useCallback(
    async (query, _, setIsLoading) => {
      setIsLoading(true);
      let newOptions = [];
      try {
        const { body, status } = await listContentTypes(jwt, space, {
          ...ctdParams,
          name: query,
        });
        checkResponseStatus(body, status);
        newOptions = body.data || [];
      } catch (error) {
        toast.error(
          t(
            error instanceof ResponseError
              ? 'ContentForm.CouldntFetch'
              : 'Form.CommunicationErrorMessage',
          ),
        );
      }

      setIsLoading(false);
      return newOptions
        .filter((ctd) => !ctd.internal || ctd.name === '_media')
        .map((ctd) => ({ label: ctd.label, value: ctd.name }));
    },
    [jwt, ctdParams, space, t],
  );

  const handleVersionRestore = useCallback(
    async (webhook) => {
      if (formState) {
        formState.setValues(webhook);
        await formState.submitForm();
      }
    },
    [formState],
  );

  const isLoading = isLoadingCT || webhookLoading || webhookContentTypeLoading;

  const isFormDisabled = useMemo(() => {
    if (isLoading || isSaving) return true;
    if (id) return !canUpdate;
    return !canCreate;
  }, [canCreate, canUpdate, id, isLoading, isSaving]);

  /**
   * @emits FlotiqPlugins."flotiq.form.sidebar-panel::add"
   */
  const pluginSidebarPanels = usePluginResults(
    'flotiq.form.sidebar-panel::add',
    FormAddSidebarPanelEvent,
    {
      contentType: webhookContentType,
      contentObject: webhook,
      disabled: isLoading,
      duplicate,
      create: !id,
      userPlugins,
      formik: formState,
    },
  );

  const isSaveAllowed = (canUpdate && id) || (canCreate && !id);

  return (
    <PageLayout
      page="webhooks"
      title={`${mode === 'add' ? t('Global.Add') : t('Global.Edit')} Webhook`}
      breadcrumbs={
        <TopbarBreadcrumbs
          parentTitle={t('Global.Webhooks')}
          parentLink={gridLink}
        />
      }
      buttons={
        <>
          <TopbarCancelButton link={gridLink} />
          <TopbarSaveButton
            form="webhook-form"
            isLoading={isLoading}
            disabled={disableSave}
            actionDisallowed={!isSaveAllowed}
          />
          <TopbarActionMenu>
            {mode === 'edit' && !duplicate && (
              <TopbarDeleteButton
                onClick={handleDeleteWebhook}
                actionDisallowed={!canDelete}
              />
            )}
          </TopbarActionMenu>
        </>
      }
      testId={testId}
    >
      <div className={predefinedLayoutClasses.withSidebar}>
        <div
          className={predefinedLayoutClasses.leftColumnWhite}
          {...getTestProps(testId, 'container')}
        >
          {isLoading && (
            <div
              className={twMerge(
                'absolute top-0 left-0 w-full h-full flex bg-white dark:bg-gray-800',
                'justify-center items-center z-10 rounded-md opacity-90',
              )}
              {...getTestProps(testId, 'container-loader')}
            >
              <Loader type="spinner-grid" size="small" />
            </div>
          )}

          <WebhookForm
            webhook={webhook}
            webhookContentType={webhookContentType}
            onSubmit={handleSave}
            mode={mode}
            userPlugins={userPlugins}
            codeWebhookAvailable={codeWebhookAvailable}
            contentTypesOptions={contentTypesOptions}
            filterCtdName={filterCtdName}
            disabled={isFormDisabled}
            setFormState={setFormState}
            testId={testId}
          />
        </div>

        <div className={predefinedLayoutClasses.rightColumn}>
          <ContentObjectInformations
            createdAt={duplicate ? null : webhook?.internal?.createdAt}
            updatedAt={duplicate ? null : webhook?.internal?.updatedAt}
            additionalClasses="order-10"
            testId={testId}
          />

          {mode === 'edit' && !duplicate && (
            <ContentObjectLogs
              logs={webhookLogs}
              loading={webhookLogsLoading}
              testId={testId}
              onRefresh={webhookLogsReload}
              additionalClasses="order-20"
            />
          )}

          {webhookVersions?.length > 0 && !duplicate && (
            <ContentObjectVersions
              objectId={id}
              contentType={webhookContentType}
              versions={webhookVersions}
              onRestore={handleVersionRestore}
              currentChanges={formState?.values}
              isDirty={formState?.dirty}
              additionalClasses="order-30"
              testId={testId}
            />
          )}

          {pluginSidebarPanels?.length > 0 && (
            <ElementFromPlugin results={pluginSidebarPanels} />
          )}
        </div>
      </div>
    </PageLayout>
  );
};

export default AddWebhooks;

AddWebhooks.propTypes = {
  /**
   * If is on duplicate
   */
  duplicate: PropTypes.bool,
  /**
   * Current mode "edit" or "add"
   */
  mode: PropTypes.string,
  /**
   * Test id for page
   */
  testId: PropTypes.string,
};
